import {
    Box,
    Card,
    CardContent,
    Typography,
    makeStyles,
} from "@material-ui/core";
import { ReactNode } from "react";
import { currencyFormatter } from "../constants";

type SimpleDashboardCardProps = {
    label: string;
    value: string | number;
    subtitle?: string;
    icon?: ReactNode;
    accent?: string;
    format?: "currency" | "number";
};

const useStyles = makeStyles((theme) => ({
    card: {
        height: "100%",
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        position: "relative",
        overflow: "hidden",
    },
    accent: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
    },
    content: {
        padding: theme.spacing(2.5),
        "&:last-child": {
            paddingBottom: theme.spacing(2.5),
        },
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: theme.spacing(1.5),
    },
    iconWrap: {
        width: 44,
        height: 44,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0.9,
    },
    label: {
        fontSize: "0.8rem",
        fontWeight: 600,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
    },
    value: {
        fontWeight: 700,
        fontSize: "1.75rem",
        lineHeight: 1.2,
        color: theme.palette.text.primary,
    },
    subtitle: {
        marginTop: theme.spacing(0.75),
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
    },
}));

const SimpleDashboardCard = ({
    label,
    value,
    subtitle,
    icon,
    accent = "#fcca4a",
    format = "currency",
}: SimpleDashboardCardProps) => {
    const classes = useStyles();
    const numericValue = Number(value) || 0;
    const displayValue =
        format === "number"
            ? numericValue.toLocaleString("en-PK")
            : currencyFormatter(numericValue);

    return (
        <Card className={classes.card} elevation={0}>
            <Box className={classes.accent} style={{ background: accent }} />
            <CardContent className={classes.content}>
                <Box className={classes.header}>
                    <Typography className={classes.label}>{label}</Typography>
                    {icon && (
                        <Box
                            className={classes.iconWrap}
                            style={{ background: `${accent}22`, color: accent }}
                        >
                            {icon}
                        </Box>
                    )}
                </Box>
                <Typography className={classes.value}>
                    {displayValue}
                </Typography>
                {subtitle && (
                    <Typography className={classes.subtitle}>{subtitle}</Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default SimpleDashboardCard;
